import { API_V1 } from 'API/APIs/APIv1';
import { isCancel } from 'axios';
import { ProductGroups } from 'enums';
import { ContactToAdd } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { AddContactsFromFunnelRequest } from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/add-contacts-from-campaign/add-contacts-from-campaign';
import { ContactToApprove, Funnel, FunnelFunctions, FunnelStatuses, FunnelTypes, LinkedInCrawlerContact, MessageToApprove } from 'features/co-pilot/co-pilot.types';
import { MessageTypes } from 'features/co-pilot/inbox/funnel-inbox.types';
import { Tasks } from 'features/co-pilot/task-manager/task-manager.types';
import _ from 'lodash';
import Logger from 'logger';

import { LEGACY_STEP_TO_FLOW_FUNCTION } from './step-names';

const normalizeFunnelSteps = (steps: any[]) => {
  return steps.map((step) => {
    if (_.isNil(step) || typeof step !== 'object') {
      return step;
    }

    const normalizedStep = { ...step } as Record<string, any>;
    const rawFunctionName = normalizedStep.functionName;
    if (typeof rawFunctionName === 'string' && LEGACY_STEP_TO_FLOW_FUNCTION[rawFunctionName]) {
      normalizedStep.functionName = LEGACY_STEP_TO_FLOW_FUNCTION[rawFunctionName];
    }

    if (_.isNil(normalizedStep.waitTillNext) && typeof normalizedStep.waitDays === 'number') {
      normalizedStep.waitTillNext = normalizedStep.waitDays * 24 * 60 * 60;
    }

    return normalizedStep;
  });
};

const normalizeFunnelResponse = <T>(payload: T): T => {
  if (_.isNil(payload) || typeof payload !== 'object') {
    return payload;
  }

  const asRecord = payload as Record<string, any>;
  const normalizeBlacklistAliases = (record: Record<string, any>) => {
    if (Array.isArray(record.blacklistIds)) {
      record.blacklists = [...record.blacklistIds];
      return record;
    }
    if (Array.isArray(record.blacklists)) {
      record.blacklistIds = [...record.blacklists];
    }
    return record;
  };
  // Mongoose's `timestamps: true` exposes `createdAt` / `updatedAt`, but the
  // legacy frontend (FunnelOverview, campaign-item, campaign-detail-view, ...)
  // reads `createDate` / `updateDate`. Without this alias the "Erstellungsdatum"
  // row renders "-" for every campaign even though the date exists server-side.
  const normalizeTimestampAliases = (record: Record<string, any>) => {
    if (_.isNil(record.createDate) && !_.isNil(record.createdAt)) {
      record.createDate = record.createdAt;
    }
    if (_.isNil(record.updateDate) && !_.isNil(record.updatedAt)) {
      record.updateDate = record.updatedAt;
    }
    return record;
  };
  const normalizeSummaryAliases = (record: Record<string, any>) => {
    if (!_.isNil(record.funnelSummary)) {
      return record;
    }

    const stats = _.isObject(record.stats) ? (record.stats as Record<string, any>) : null;
    if (_.isNil(stats)) {
      return record;
    }

    return {
      ...record,
      funnelSummary: {
        totalContactsCount: stats.totalContacts || 0,
        activeContactsCount: stats.activeContacts || 0,
        contactedContactsCount: stats.messagesSent || 0,
        replyAndAcceptCount: stats.replyAndAcceptCount ?? ((stats.repliedContacts || 0) + (stats.connectionsAccepted || 0)),
        sentConnectionRequestsCount: stats.connectionsSent || 0,
        acceptedConnectionRequestsCount: stats.connectionsAccepted || 0,
        messageReplyCount: stats.repliedContacts || 0,
        positiveReplyCount: stats.positiveReplies || 0,
        negativeReplyCount: stats.negativeReplies || 0,
        finishedContactsCount: stats.finishedContacts || 0,
        connectedContactsCount: stats.connectionsAccepted || 0,
        unsentContactsCount: stats.unsentContacts || 0,
        searchContactsRemaining: stats.searchContactsRemaining || 0,
        contactSupply: stats.contactSupply || 'none',
      },
    };
  };

  if (
    !_.isNil(asRecord.linkedinAccountId) &&
    (_.isNil(asRecord.linkedinAccount) || asRecord.linkedinAccount === '')
  ) {
    const normalized = normalizeTimestampAliases(normalizeSummaryAliases(normalizeBlacklistAliases({
      ...asRecord,
      linkedinAccount: asRecord.linkedinAccountId,
    } as Record<string, any>)));
    if (Array.isArray(normalized.steps)) {
      normalized.steps = normalizeFunnelSteps(normalized.steps);
    }
    return normalized as T;
  }

  if (Array.isArray(asRecord.steps)) {
    return normalizeTimestampAliases(normalizeSummaryAliases(normalizeBlacklistAliases({
      ...asRecord,
      steps: normalizeFunnelSteps(asRecord.steps),
    }))) as T;
  }

  return normalizeTimestampAliases(normalizeSummaryAliases(normalizeBlacklistAliases({ ...asRecord }))) as T;
};

const normalizeFunnelUpdatePayload = <T extends Record<string, any>>(payload: T): T => {
  const normalized = {
    ...payload,
  } as Record<string, any>;

  if (!_.isNil(payload?.linkedinAccount)) {
    normalized.linkedinAccountId = payload.linkedinAccount;
    delete normalized.linkedinAccount;
  }
  if (Array.isArray(payload?.blacklists) && !Array.isArray(payload?.blacklistIds)) {
    normalized.blacklistIds = payload.blacklists;
    delete normalized.blacklists;
  }

  return normalized as T;
};

export const getAllFunnels = async (
  type?: FunnelTypes,
  q?: string,
  page?: number,
  total?: number,
  filter?: string,
  linkedinAccountFilter?: string[],
  campaignStatusFilter?: string[],
  basicInfo?: boolean
) => {
  try {
    const params: any = {};

    if (!_.isNil(type)) {
      params.type = type;
    }
    if (!_.isNil(q) && q !== '') {
      params.q = q;
    }
    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }
    if (!_.isNil(filter)) {
      params.filter = filter;
    }
    if (!_.isNil(linkedinAccountFilter)) {
      params.linkedinAccounts = linkedinAccountFilter.join(';') || undefined;
    }
    if (!_.isNil(campaignStatusFilter)) {
      params.funnelStatuses = campaignStatusFilter.join(';') || undefined;
    }
    if (!_.isNil(basicInfo)) {
      params.basicInfo = basicInfo;
    }

    const response = await API_V1.get('/campaigns', {
      params,
    });

    const responseData = response.data;
    if (Array.isArray(responseData?.campaigns)) {
      return {
        ...responseData,
        campaigns: responseData.campaigns.map((campaign: any) => normalizeFunnelResponse(campaign)),
      };
    }

    return responseData;
  } catch (e) {
    Logger.trace('Error while getting all funnels!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createFunnel = async (requestBody: {
  name: string;
  type: FunnelTypes.ACTIVE_SOURCING_CO_PILOT | FunnelTypes.SOCIAL_SELLING_CO_PILOT;
  linkedinAccountId: string;
  funnelContacts?: LinkedInCrawlerContact[];
  offer?: { [key: string]: any };
  activeSourcingID?: string;
  socialSellingID?: string;
  settings?: {
    language?: 'de' | 'en' | 'auto';
    secondaryLanguage?: 'de' | 'en' | 'none';
    formalSpeech?: boolean;
    includeEmoji?: boolean;
    approveMessages?: boolean;
    swissS?: boolean;
  };
}) => {
  try {
    const response = await API_V1.post('/campaigns', {
      ...requestBody,
      linkedInAccountId: requestBody.linkedinAccountId,
    });

    return normalizeFunnelResponse(response.data);
  } catch (e) {
    Logger.trace('Error while creating a new funnel!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const cloneFunnel = async (funnelId: string, name: string, cloneContacts: boolean = false) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/clone`, {
      name,
      cloneContacts,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while cloning funnel!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateFunnel = async (
  funnelId: string,
  funnelData: Partial<Funnel | { addContacts: LinkedInCrawlerContact[] | ContactToAdd[] } | { removeContacts: string[] }>,
  signal?: AbortSignal
) => {
  try {
    const normalizedPayload = normalizeFunnelUpdatePayload(funnelData as Record<string, any>);
    const response = await API_V1.patch(`/campaigns/${funnelId}`, normalizedPayload, {
      signal,
    });

    return normalizeFunnelResponse(response.data);
  } catch (e) {
    Logger.trace('Error while updating funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCampaignTemplates = async (type?: FunnelTypes) => {
  try {
    const response = await API_V1.get('/campaigns/templates', {
      params: { type },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error fetching campaign templates!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const applyFunnelTemplate = async (funnelId: string, templateKey: string, _type: FunnelTypes) => {
  try {
    const response = await API_V1.put(`/campaigns/${funnelId}/apply-template`, { templateId: templateKey });

    return response.data;
  } catch (e) {
    Logger.trace('Error while applying template to funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * Flow-Builder (Schritt 3): what the builder already knows about the campaign and which questions it must ask.
 */
export const getFlowBuilderContext = async (funnelId: string) => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}/flow-builder`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while loading flow-builder context for funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Flow-Builder (Schritt 3): generate steps, canvas and auto-reply from the answers.
 */
export const applyFlowBuilder = async (funnelId: string, answers: Record<string, unknown>) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/flow-builder`, answers);
    const data = response.data ?? {};

    return { ...data, campaign: normalizeFunnelResponse(data.campaign) };
  } catch (e) {
    Logger.trace('Error while applying the flow builder to funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getFunnelById = async (funnelId: string) => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}`);

    return normalizeFunnelResponse(response.data);
  } catch (e) {
    Logger.trace('Error while getting funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteFunnel = async (funnelId: string) => {
  try {
    const response = await API_V1.delete(`/campaigns/${funnelId}`);
    // DELETE /campaigns/:id returns 204 No Content on success.
    // Axios represents an empty body as "" (not null/undefined), so `??` is not
    // sufficient here – callers check `if (res && !res.error)` and would treat
    // an empty string as failure. Always return a truthy success object when
    // the HTTP status indicates success and the body is empty.
    if (response.data === undefined || response.data === null || response.data === '') {
      return { success: true };
    }
    return response.data;
  } catch (e) {
    Logger.trace('Error while deleting funnel %s!', funnelId);
    Logger.error(e);
    const backendError = _.get(e, 'response.data');
    const normalizedError =
      !_.isNil(backendError) && typeof backendError === 'object'
        ? backendError
        : {
            code: _.get(e, 'response.status', _.get(e, 'code', 'UNKNOWN_ERROR')),
            message: _.get(e, 'response.data.message', _.get(e, 'response.data.error.message', _.get(e, 'message', 'Unknown error'))),
          };

    return { error: normalizedError, data: [] };
  }
};

export const testCampaign = async (funnelId: string, linkedinUrls: string[]) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/test`,
      {
        linkedInURLs: linkedinUrls,
      },
      {
        'axios-retry': {
          retries: 0,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while testing funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/** Letzter Test der Kampagne — egal ob in der App oder per MCP gestartet (2 h aufbewahrt). */
export const getLatestCampaignTest = async (funnelId: string) => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}/test/latest`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while loading the latest test of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), response: null };
  }
};

export const startFunnel = async (funnelId: string) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/status`,
      { status: 'active' },
      {
        'axios-retry': {
          retries: 0,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while starting funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const testPrompt = async (
  funnelId: string,
  functionName: FunnelFunctions,
  prompt: string,
  addSignature: boolean = false,
  testContact?: string,
  signal?: AbortSignal
) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/testFunctionPrompt`,
      {
        functionName,
        prompt,
        addSignature,
        testContact,
      },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while testing a function prompt!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const changeFunnelStatus = async (funnelId: string, status: FunnelStatuses) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/status`, {
      status,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while changing status of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

const getLocalMidnightDate = (date: Date) => {
  const localMidnight = new Date(date);
  localMidnight.setHours(0, 0, 0, 0);

  return new Date(localMidnight.getTime() - localMidnight.getTimezoneOffset() * 60000);
};

const getLocalLastMinuteDate = (date: Date) => {
  const localLastMinute = new Date(date);
  localLastMinute.setHours(23, 59, 59, 999);

  return new Date(localLastMinute.getTime() - localLastMinute.getTimezoneOffset() * 60000);
};

export const getStatisticsPerformanceOverview = async (
  funnelIds: string[],
  duration?: { from: Date; to: Date } | string,
  signal?: any
) => {
  try {
    const response = await API_V1.post('/analytics/performance/overview',
      { funnelIds, duration },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting performance overview!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getMessageStatistics = async (funnelIds: string[], duration?: { from: Date; to: Date } | string, signal?: any) => {
  const buildStatisticsFromHistory = async () => {
    const historyResponse = await API_V1.post(
      '/messages/history',
      { funnelIds, duration },
      { signal }
    );

    const contacts = Array.isArray(historyResponse.data) ? historyResponse.data : [];
    const dateSet = new Set<string>();
    const seriesByStep = new Map<string, Map<string, number>>();

    for (const contact of contacts) {
      const messages = Array.isArray(contact?.messages) ? contact.messages : [];
      for (const message of messages) {
        if (message?.direction !== 'sent') continue;
        if (!message?.date) continue;

        const rawStepName = message?.step?.functionName;
        const stepName =
          typeof rawStepName === 'string' && LEGACY_STEP_TO_FLOW_FUNCTION[rawStepName]
            ? LEGACY_STEP_TO_FLOW_FUNCTION[rawStepName]
            : rawStepName;

        if (typeof stepName !== 'string' || stepName.length === 0) continue;

        const date = new Date(message.date);
        if (Number.isNaN(date.getTime())) continue;
        const dateKey = date.toISOString().split('T')[0];
        dateSet.add(dateKey);

        if (!seriesByStep.has(stepName)) {
          seriesByStep.set(stepName, new Map());
        }
        const stepSeries = seriesByStep.get(stepName)!;
        stepSeries.set(dateKey, (stepSeries.get(dateKey) || 0) + 1);
      }
    }

    let dates = Array.from(dateSet).sort((a, b) => a.localeCompare(b));
    if (typeof duration === 'object' && duration?.from && duration?.to) {
      const cursor = new Date(duration.from);
      const end = new Date(duration.to);
      cursor.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      dates = [];
      while (cursor <= end) {
        dates.push(cursor.toISOString().split('T')[0]);
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    const response: Record<string, any> = { dates };
    for (const [stepName, stepSeries] of seriesByStep.entries()) {
      response[stepName] = dates.map((dateKey) => stepSeries.get(dateKey) || 0);
    }

    return { response };
  };

  const isStepTimeSeries = (value: any) => {
    const payload = value?.response ?? value;
    return !!payload && Array.isArray(payload.dates);
  };

  try {
    if (duration) {
      if (typeof duration === 'object') {
        duration.from = getLocalMidnightDate(duration.from);
        duration.to = getLocalLastMinuteDate(duration.to);
      }
    }

    try {
      const response = await API_V1.post(
        '/analytics/messages/statistics',
        { funnelIds, duration },
        { signal }
      );
      if (isStepTimeSeries(response.data)) {
        return response.data;
      }
    } catch {
      // If the analytics route is unavailable, derive stable chart data below.
    }

    return await buildStatisticsFromHistory();
  } catch (e) {
    Logger.trace('Error while getting message statistics!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getMessageHistory = async (funnelIds: string[], duration?: { from: Date; to: Date } | string, signal?: any) => {
  try {
    const response = await API_V1.post('/messages/history',
      { funnelIds, duration },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting message history!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getPerformanceComparison = async (funnelIds: string[], duration?: { from: Date; to: Date } | string, signal?: any) => {
  try {
    const response = await API_V1.post('/analytics/performance/comparison',
      { funnelIds, duration },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting performance comparison!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getFlowStatistics = async (funnelId: string, signal?: any) => {
  try {
    const response = await API_V1.post('/analytics/flow-statistics',
      { funnelId },
      {
        signal,
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting flow statistics!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getInboxFunnels = async (
  type: string | undefined,
  searchParam: string,
  funnelFilter: string[],
  liAccountFilter: string[],
  signal?: any
) => {
  try {
    const params: any = {};

    if (type) {
      params.type = type;
    }
    if (searchParam) {
      params.q = searchParam;
    }
    if (!_.isNil(funnelFilter) && !_.isEmpty(funnelFilter)) {
      params.funnelFilter = funnelFilter.join(';');
    }
    if (!_.isNil(liAccountFilter) && !_.isEmpty(liAccountFilter)) {
      params.liAccountFilter = liAccountFilter.join(';');
    }

    const response = await API_V1.get('/messages/inbox', {
      params,
      'axios-retry': {
        retries: 0,
      },
      signal,
    });

    return response.data;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting inbox funnels!');
    Logger.error(error);
    throw error;
  }
};

export interface InboxUpdateTriggerResult {
  requestedAt: string;
  accountIds: string[];
  accountsQueued: number;
}

export interface InboxUpdateStatus {
  done: boolean;
  updatedCount: number;
  totalCount: number;
  pendingAccountIds: string[];
}

/**
 * Trigger an ad-hoc inbox update. Always returns immediately with the list of
 * queued account IDs and a `requestedAt` timestamp — use getInboxUpdateStatus
 * to poll for completion.
 */
export const updateInboxFunnels = async (
  type: string | undefined,
  searchParam: string,
  funnelFilter: string[],
  liAccountFilter: string[],
  signal?: any,
  since?: string,
): Promise<InboxUpdateTriggerResult> => {
  const params: any = {};

  if (type) params.type = type;
  if (searchParam) params.q = searchParam;
  if (!_.isNil(funnelFilter) && !_.isEmpty(funnelFilter)) {
    params.funnelFilter = funnelFilter.join(';');
  }
  if (!_.isNil(liAccountFilter) && !_.isEmpty(liAccountFilter)) {
    params.liAccountFilter = liAccountFilter.join(';');
  }
  if (since) params.since = since;

  const response = await API_V1.post(
    '/messages/inbox/update',
    {},
    {
      params,
      'axios-retry': { retries: 0 },
      signal,
    }
  );

  return response.data;
};

/**
 * Poll the completion status of a previously triggered inbox update.
 * Pass the `requestedAt` and `accountIds` returned by updateInboxFunnels.
 */
export const getInboxUpdateStatus = async (
  accountIds: string[],
  since: string,
  signal?: any
): Promise<InboxUpdateStatus> => {
  const response = await API_V1.get('/messages/inbox/update/status', {
    params: {
      accountIds: accountIds.join(';'),
      since,
    },
    'axios-retry': { retries: 0 },
    signal,
  });

  return response.data;
};

export const getFunnelInboxContacts = async (funnelId: string, signal?: any) => {
  try {
    const response = await API_V1.get(`/messages/inbox/${funnelId}/contacts`, {
      'axios-retry': {
        retries: 0,
      },
      signal,
    });

    return response.data;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting funnel %s inbox contacts!', funnelId);
    Logger.error(error);
    throw error;
  }
};

export const getFunnelInboxMessages = async (funnelId: string, profileId: string, signal?: any) => {
  try {
    const response = await API_V1.get(`/messages/inbox/${funnelId}/contacts/${profileId}/messages`, {
      'axios-retry': {
        retries: 0,
      },
      signal,
    });

    return response.data;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting funnel %s inbox messages!', funnelId);
    Logger.error(error);
    throw error;
  }
};

export const replyFromInbox = async (
  profileId: string,
  funnelId: string,
  messageText: string,
  messageType?: MessageTypes,
  lastMessageText?: string,
  linkedInURL?: string
) => {
  try {
    const response = await API_V1.post('/messages/reply', {
      profileId,
      funnelId,
      messageText,
      messageType,
      inReplyToEmail: lastMessageText,
      linkedInURL,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while sending reply message from inbox!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const toggleMarkAsRead = async (funnelId: string, profileId: string, markedAsRead: boolean) => {
  try {
    const response = await API_V1.put(`/messages/inbox/${funnelId}/contacts/${profileId}/toggleMarkAsRead`, {
      markedAsRead,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while setting funnel contact to conversation finished!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getToBeApprovedMessages = async (funnelId: string) => {
  try {
    const response = await API_V1.get(`/messages/campaign/${funnelId}/pending`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching to be approved messages of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateToBeApprovedMessages = async (funnelId: string, messages: MessageToApprove[]) => {
  try {
    const response = await API_V1.post('/messages/bulk-approve', { campaignId: funnelId, messages });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating to be approved messages of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getContactsToApprove = async (
  funnelId: string,
  limit = 200
): Promise<{ contacts: ContactToApprove[]; total: number; error?: unknown }> => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}/contact-approvals`, { params: { limit } });

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching contacts to approve of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), contacts: [], total: 0 };
  }
};

/** Freigeben oder ablehnen; `'all'` meint alle wartenden Kontakte der Kampagne. */
export const decideContactsToApprove = async (
  funnelId: string,
  campaignContactIds: string[] | 'all',
  approve: boolean
): Promise<{ updated: number; remaining: number; error?: unknown }> => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/contact-approvals`, { campaignContactIds, approve });

    return response.data;
  } catch (e) {
    Logger.trace('Error while deciding contacts to approve of funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), updated: 0, remaining: -1 };
  }
};

export const getPerplexitySummary = async (url: string, variant: ProductGroups, language: string) => {
  try {
    const response = await API_V1.get('/ai-builders/perplexity-summary', {
      params: {
        url,
        activeSourcingPilot: variant === ProductGroups.HR_BOOSTER,
        socialSellingPilot: variant === ProductGroups.LEAD_BOOSTER,
        language,
      },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error fetching Perplexity summary!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getDetailedContactView = async (linkedinUrl?: string, profileId?: string, funnelId?: string) => {
  try {
    const response = await API_V1.get('/contacts/detailed-view', {
      params: {
        linkedinUrl,
        profileId,
        funnelId,
      },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error fetching detail contact view of %s!', profileId || linkedinUrl || 'url not found');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getSavedTranscript = async (
  profileId: string,
  tenant: string,
  funnelId: string,
  stepIndex: string,
  lang: string
) => {
  try {
    const response = await API_V1.get(
      `/tasks/call-transcripts?profileId=${encodeURIComponent(profileId)}&tenant=${encodeURIComponent(tenant)}&funnelId=${encodeURIComponent(
        funnelId
      )}&stepIndex=${encodeURIComponent(stepIndex)}&lang=${encodeURIComponent(lang)}`
    );
    return response.data;
  } catch (e) {
    Logger.trace('Error fetching saved transcript!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const handleCallTranscriptOutcome = async (
  profileId: string,
  tenant: string,
  result: string,
  funnelId: string,
  stepIndex: string
) => {
  try {
    const response = await API_V1.post('/tasks/call-transcripts/outcome', {
      profileId,
      tenant,
      result,
      funnelId,
      stepIndex,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error sending call transcript outcome!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getTaskManagerTasks = async (
  type?: string,
  taskFilter?: string,
  liAccountFilter?: string[],
  getCounts?: boolean,
  signal?: AbortSignal
): Promise<Tasks | undefined> => {
  try {
    const params: Record<string, any> = {};

    if (type) params.type = type;
    if (taskFilter) params.taskFilter = taskFilter;
    if (liAccountFilter?.length) params.liAccountFilter = liAccountFilter.join(';');
    if (getCounts) params.getCounts = true;

    const response = await API_V1.get<{ response: Tasks }>('/tasks', {
      params,
      'axios-retry': { retries: 0 },
      signal,
    });

    return response.data?.response;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting task manager tasks!');
    Logger.error(error);
    throw error;
  }
};

export const addContactsFromCampaign = async (funnelId: string, payload: AddContactsFromFunnelRequest) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/add-contacts-from-campaign`, payload);

    return response.data;
  } catch (e) {
    Logger.trace('Error while adding contacts from campaign!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getWorkUnitsStatus = async (funnelId?: string, signal?: AbortSignal) => {
  try {
    const url = funnelId ? `/campaigns/${funnelId}/work-units/status` : '/campaigns/work-units/status';
    const response = await API_V1.get(url, {
      'axios-retry': { retries: 0 },
      signal,
    });

    return response.data;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting work units status!');
    Logger.error(error);
    throw error;
  }
};

export const deleteWorkUnit = async (itemId: string) => {
  try {
    const response = await API_V1.delete(`/campaigns/work-units/${itemId}`, {
      'axios-retry': { retries: 0 },
    });

    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting work unit %s!', itemId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// Admin Monitoring APIs
export const getAdminWorkUnitsStatus = async (limit?: number, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get('/admin/work-units/status', {
      params: limit ? { limit } : undefined,
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    Logger.trace('Error while getting admin work units status!');
    Logger.error(error);
    throw error;
  }
};

export const getAdminDailyLimits = async (signal?: AbortSignal) => {
  try {
    const response = await API_V1.get('/admin/daily-limits', {
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    Logger.trace('Error while getting admin daily limits!');
    Logger.error(error);
    throw error;
  }
};

export const manageAdminWorkUnit = async (workItemId: string, action: 'remove' | 'finish', reason?: string) => {
  try {
    const response = await API_V1.put(
      `/admin/work-units/${workItemId}`,
      { action, reason },
      {
        'axios-retry': { retries: 0 },
      }
    );
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    Logger.trace('Error while managing admin work unit %s!', workItemId);
    Logger.error(error);
    throw error;
  }
};

// Fetch admin view of a LinkedIn account with its funnels (debug endpoint)
export const getAdminAccountFunnels = async (tenantId: string, accountId: string, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get(`/admin/account/${tenantId}/${accountId}/funnels`, {
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    Logger.trace('Error while getting admin account funnels!');
    Logger.error(error);
    throw error;
  }
};

/**
 * Test the auto-reply logic for a funnel with a given chat transcript.
 */
export const testAutoreply = async (funnelId: string, chatTranscript: string) => {
  try {
    const response = await API_V1.post('/campaigns/test-autoreply', {
      funnelId,
      chatTranscript,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while testing auto-reply for funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const abortFunnel = async (funnelId: string) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/status`,
      { status: 'paused' },
      {
        'axios-retry': {
          retries: 0,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while aborting funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * Starts an ad-hoc profile analysis for the given funnel.
 */
export const startAdhocProfileAnalysis = async (funnelId: string) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/adhoc/analyze`,
      {},
      {
        'axios-retry': { retries: 0 },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while starting ad-hoc profile analysis for funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * Stops queued profile analysis jobs (ad-hoc and search-crawl enrichment) for the given funnel.
 */
export const stopAdhocProfileAnalysis = async (funnelId: string) => {
  try {
    const response = await API_V1.post(
      `/campaigns/${funnelId}/adhoc/analyze/stop`,
      {},
      {
        'axios-retry': { retries: 0 },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while stopping ad-hoc profile analysis for funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// Add helper to fetch enriched contacts message history (flattened format)
export const getContactsMessageHistory = async (
  funnelId: string,
  filter: string = 'isActive',
  limit: number = 10000,
  offset: number = 0,
  signal?: AbortSignal
) => {
  try {
    const params: any = { filter, limit, offset };

    const response = await API_V1.get(`/campaigns/${funnelId}/contactsMessageHistory`, {
      params,
      signal,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting contacts message history for funnel %s!', funnelId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// ===========================================================================
// Admin Queue Management APIs
// ===========================================================================

export const getAdminQueueStats = async (signal?: AbortSignal) => {
  try {
    const response = await API_V1.get('/admin/queues/stats', {
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    throw error;
  }
};

export const getAdminFailedJobs = async (queueName: string, start = 0, limit = 50, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get(`/admin/queues/${queueName}/failed`, {
      params: { start, limit },
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    throw error;
  }
};

export const retryAdminJob = async (queueName: string, jobId: string) => {
  const response = await API_V1.post(`/admin/queues/${queueName}/jobs/${jobId}/retry`);
  return response.data;
};

export const deleteAdminJob = async (queueName: string, jobId: string) => {
  const response = await API_V1.delete(`/admin/queues/${queueName}/jobs/${jobId}`);
  return response.data ?? { success: true };
};

export const retryAllAdminFailedJobs = async (queueName: string) => {
  const response = await API_V1.post(`/admin/queues/${queueName}/retry-all`);
  return response.data;
};

export const deleteAllAdminFailedJobs = async (queueName: string) => {
  const response = await API_V1.delete(`/admin/queues/${queueName}/failed`);
  return response.data ?? { success: true };
};

export const getAdminLinkedinSessionMonitor = async (hours = 24, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get('/admin/monitoring/linkedin-session-expiry', {
      params: { hours },
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    throw error;
  }
};

export const getCampaignContactEvents = async (
  campaignId: string,
  contactId: string,
  limit = 200,
  signal?: AbortSignal,
) => {
  try {
    const response = await API_V1.get(`/campaigns/${campaignId}/contacts/${contactId}/events`, {
      params: { limit },
      'axios-retry': { retries: 0 },
      signal,
    });
    return response.data;
  } catch (error) {
    if (isCancel(error)) return;
    throw error;
  }
};

